import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { fcmService } from '../services/fcmService';

export const useFCM = () => {
  const { user } = useAuth();
  const [permission, setPermission] = useState<NotificationPermission>(() =>
    fcmService.checkPermission()
  );
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!user?.id) {
      setToken(null);
      return;
    }
    const currentPermission = fcmService.checkPermission();
    setPermission(currentPermission);
    if (currentPermission !== 'granted') return;
    setLoading(true);
    fcmService
      .getToken(user.id)
      .then(value => {
        if (!cancelled) setToken(value);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  useEffect(() => {
    if (permission !== 'granted') return;
    return fcmService.onForegroundMessage(payload => {
      const title = payload.notification?.title || payload.data?.title || 'إشعار جديد';
      const body = payload.notification?.body || payload.data?.body || '';
      toast.success(`${title}\n${body}`, { duration: 5000, icon: '🔔' });
    });
  }, [permission]);

  const requestPermission = async () => {
    if (!user?.id) return false;
    setLoading(true);
    try {
      const nextPermission = await fcmService.requestPermission();
      setPermission(nextPermission);
      if (nextPermission !== 'granted') {
        toast.error('لم يتم منح إذن الإشعارات.');
        return false;
      }
      const nextToken = await fcmService.getToken(user.id);
      setToken(nextToken);
      if (!nextToken) {
        toast.error('تعذر تسجيل هذا الجهاز لاستقبال الإشعارات.');
        return false;
      }
      toast.success('تم تفعيل الإشعارات بنجاح.');
      return true;
    } catch {
      toast.error('حدث خطأ أثناء تفعيل الإشعارات.');
      return false;
    } finally {
      setLoading(false);
    }
  };

  return {
    permission,
    token,
    loading,
    requestPermission,
    isGranted: permission === 'granted',
    isDenied: permission === 'denied',
    isDefault: permission === 'default',
  };
};

export default useFCM;
